import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MoonStar } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Contact";
import { Asterisk, EASE, Reveal } from "@/components/site/Reveal";
import { usePageMeta } from "@/lib/usePageMeta";

const EMAIL = "nbenachour@milestonepediatrictherapyllc.com";

const PROGRAMS = [
  {
    name: "In-Clinic ABA",
    ages: "24 mo – 8 yrs",
    text: "Daily clinic-based ABA programs for our youngest learners — foundational communication, play, early learning, and school-readiness skills.",
    img: "https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Young child playing and painting",
  },
  {
    name: "School-Age ABA",
    ages: "As Requested",
    text: "After-school support for classroom participation, social skills, behavior, and growing independence.",
    img: "https://images.unsplash.com/photo-1509062522246-3755977927d7?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "School-age children in a classroom raising their hands",
  },
  {
    name: "Teen ABA",
    ages: "As Requested",
    text: "Self-advocacy, social confidence, daily living skills, and greater independence.",
    img: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Group of teenagers laughing together",
  },
  {
    name: "Social Skills Groups",
    ages: "As Requested",
    text: "Ages 5–18, grouped by age and skill level to encourage meaningful, appropriate peer interaction.",
    img: "https://images.unsplash.com/photo-1543269865-cbf427effbad?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Group of students working together",
    link: "/social-skills-group",
  },
  {
    name: "Telehealth ABA",
    ages: "As Requested",
    text: "Secure virtual ABA sessions, caregiver guidance, and consultation — quality care from the comfort of home.",
    img: "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?crop=entropy&cs=srgb&fm=jpg&q=85",
    alt: "Virtual therapy session on a laptop",
    id: "telehealth",
  },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function AgesPrograms() {
  usePageMeta(
    "Ages & Programs We Serve | Milestone Pediatric Therapy",
    "ABA programs for every age — Early Learners (2–5), School-Age (5–12), Teen ABA (13–18), Social Skills Groups (5–18), and an 18+ evening program. Not limited to pediatrics."
  );
  return (
    <div data-testid="ages-programs-page" className="min-h-screen bg-background">
      <Nav />
      <main>
        <section className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-24">
          <div className="absolute -top-32 -right-40 h-[30rem] w-[30rem] rounded-full bg-brand-pale/80 blur-3xl" aria-hidden="true" />
          <div className="absolute top-1/2 -left-48 h-80 w-80 rounded-full bg-brand-yellow/30 blur-3xl" aria-hidden="true" />
          <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE }}
              className="inline-flex items-center gap-2.5 rounded-full border border-brand-lightblue/60 bg-white/70 backdrop-blur px-5 py-2 mb-8"
              data-testid="ages-eyebrow"
            >
              <Asterisk className="h-3.5 w-3.5 text-brand-coral" />
              <span className="text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-brand-blue">
                Programs by Age
              </span>
            </motion.div>

            <h1 className="font-serif font-medium tracking-tight text-brand-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.04] max-w-4xl" data-testid="ages-heading">
              <MaskedLine delay={0.15}>Ages & Programs</MaskedLine>
              <MaskedLine delay={0.3}>
                <em className="text-brand-coral">We Serve</em>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
              className="mt-7 max-w-2xl text-base md:text-lg leading-relaxed text-brand-muted"
              data-testid="ages-subcopy"
            >
              Our services are not limited to pediatrics. We offer daily programs for our pediatric
              clients, and we also support teens and adults with individualized ABA programs designed
              for every stage of life.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
              className="mt-9 flex flex-wrap gap-4"
              data-testid="ages-schedule-chips"
            >
              <span className="inline-flex items-center gap-2.5 rounded-full bg-white border border-brand-lightblue/50 px-6 py-3 text-sm font-semibold text-brand-blue shadow-soft">
                <Asterisk className="h-3.5 w-3.5 text-brand-coral" />
                Pediatric Programs: Daily, 7:30 AM – 5:00 PM
              </span>
              <span className="inline-flex items-center gap-2.5 rounded-full bg-white border border-brand-lightblue/50 px-6 py-3 text-sm font-semibold text-brand-blue shadow-soft">
                <Asterisk className="h-3.5 w-3.5 text-brand-coral" />
                School-Age & Adult Programs: 6:00 PM – 7:30 PM
              </span>
            </motion.div>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-white/60 border-y border-brand-lightblue/30">
          <div className="max-w-5xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="flex items-center gap-3 mb-5">
                <Asterisk className="h-4 w-4 text-brand-coral" />
                <span className="text-sm uppercase tracking-[0.22em] font-medium text-brand-coral">
                  Programs & Suggested Ages
                </span>
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl font-medium tracking-tight text-brand-blue mb-14" data-testid="programs-heading">
                A program for <em className="text-brand-coral">every stage.</em>
              </h2>
            </Reveal>

            <Reveal>
              <div className="rounded-[2rem] bg-white border border-brand-lightblue/40 shadow-lift overflow-hidden" data-testid="programs-list">
                {PROGRAMS.map((p) => (
                  <div
                    key={p.name}
                    id={p.id}
                    data-testid={`program-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="flex flex-wrap items-center justify-between gap-4 px-8 py-7 border-b border-brand-lightblue/20 last:border-b-0 transition-colors duration-300 hover:bg-brand-pale/30"
                  >
                    <div className="flex items-center gap-6 flex-1 min-w-0">
                      <img
                        src={p.img}
                        alt={p.alt}
                        loading="lazy"
                        className="h-24 w-24 md:h-28 md:w-28 rounded-2xl object-cover shadow-soft shrink-0"
                      />
                      <div className="max-w-xl">
                        <h3 className="font-serif text-2xl font-semibold text-brand-blue">{p.name}</h3>
                        <p className="mt-1.5 text-sm md:text-base leading-relaxed text-brand-muted">{p.text}</p>
                        {p.link && (
                          <Link
                            to={p.link}
                            data-testid="program-link-social-skills"
                            className="group mt-3 inline-flex items-center gap-2 text-brand-coral font-semibold text-sm transition-colors duration-300 hover:text-[#e05f5f]"
                          >
                            View group details & pricing
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                          </Link>
                        )}
                      </div>
                    </div>
                    <span className="inline-flex items-center rounded-full bg-brand-pale border border-brand-lightblue/50 text-brand-blue text-sm font-semibold px-6 py-2.5 whitespace-nowrap">
                      Ages {p.ages}
                    </span>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.15} className="mt-10">
              <div className="rounded-[2rem] bg-brand-blue text-white shadow-lift p-10 md:p-12" data-testid="program-evening-18plus">
                <div className="flex flex-wrap items-center justify-between gap-8">
                  <img
                    src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85"
                    alt="Adults collaborating in an evening group session"
                    data-testid="adult-program-image"
                    loading="lazy"
                    className="h-40 w-40 md:h-48 md:w-48 rounded-3xl object-cover shadow-lift shrink-0"
                  />
                  <div className="max-w-xl flex-1 min-w-64">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15 text-brand-yellow mb-6">
                      <MoonStar className="h-6 w-6" />
                    </span>
                    <h3 className="font-serif text-3xl font-semibold mb-3">
                      Evening Social Skills ABA Program
                    </h3>
                    <p className="text-white/85 text-sm md:text-base leading-relaxed">
                      An evening group designed for adults building social confidence, communication,
                      and meaningful connection — because growth doesn't stop at 18. Evenings, 6:00 PM
                      – 7:30 PM.
                    </p>
                    <a
                      href="/adult-social-skills"
                      data-testid="adult-card-link"
                      className="group mt-5 inline-flex items-center gap-2 text-brand-yellow font-semibold text-sm transition-colors duration-300 hover:text-white"
                    >
                      View program details
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </a>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-brand-yellow text-brand-ink text-sm font-bold px-7 py-3 whitespace-nowrap">
                    Ages 18+
                  </span>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <p className="mt-10 text-sm md:text-base leading-relaxed text-brand-muted text-center max-w-2xl mx-auto" data-testid="ages-note">
                Suggested ages are a guide, not a rule. Every program is individualized based on each
                person's needs, goals, and BCBA clinical assessment.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <Reveal>
              <div className="rounded-[2.5rem] bg-white border border-brand-lightblue/40 shadow-lift p-10 md:p-16 text-center" data-testid="ages-cta">
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-brand-blue mb-5">
                  Not sure which program <em className="text-brand-coral">fits?</em>
                </h2>
                <p className="max-w-2xl mx-auto text-brand-muted text-base md:text-lg leading-relaxed mb-9">
                  Contact us and we'll help you find the right starting point for your child, teen,
                  or family member.
                </p>
                <a
                  href={`mailto:${EMAIL}?subject=Program%20%26%20Age%20Question`}
                  data-testid="ages-cta-email-button"
                  className="group inline-flex items-center gap-2 rounded-full bg-brand-coral text-white font-semibold px-8 py-4 shadow-soft transition-[transform,box-shadow,background-color] duration-300 hover:-translate-y-1 hover:shadow-lift hover:bg-[#f06666]"
                >
                  <Mail className="h-4 w-4" />
                  {EMAIL}
                </a>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
